import { Form, Tooltip, Radio, Button } from 'antd';
import BaseModal from 'components/BaseModal'
import { MODAL_SIZE_MEDIUM } from 'constants/Layout';
import _ from 'lodash'
import React, { forwardRef, useState, useImperativeHandle, useRef, useMemo } from 'react'
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import { FieldTypes, nameRegexValidator, urlRegexValidator } from 'utils/formUtils';
import ModalUtil from 'utils/modalUtil';
import JiraMappingForm from '../jira/JiraMappingForm';
import PagerDutyOAuthPopup from './PagerDutyOAuthPopup';
import message from 'components/CustomMessage';
import { generateFormFields } from 'utils/formUtils';
import { HelpIcon } from 'components/Icons';
import { duplicateNameValidator } from 'utils/validatorUtil';
import { useActiveUserContext } from 'contexts/ActiveUserContext';
import { hasPermission } from 'utils/authService';
import { PermissionSetMap } from 'constants/RBACConstants';
import FreshServiceMappingForm from '../FreshService/FreshServiceMappingForm';
const freshServiceNote = "Username/Password-based authentication is not supported for accounts created after 31-Aug-2021.";

function freshServiceNoticePopOver(freshServiceNote){
    return <Tooltip title={`Note :  ${freshServiceNote}`} placement="top" overlayStyle={{width: "400px"}}>
        <HelpIcon />
    </Tooltip>
}

/**
 * 
 * @param fields provide fields to be rendered in the form
 * @param handleMapping function to handle mapping after login form submission
 */
function CommonTicketingForm({name, loginConfigList, handleMapping, fields, ...props}, ref){
    const isEdit = !_.isUndefined(props.config);
    const [form] = Form.useForm();
    const [auth, setAuth] = useState(() => props.config?.tokenType === "ACCESS_TOKEN" ? 1 : 0);
    //initially set 'Token Auth' radio checked for PagerDuty
    //1 = OAuth Radio value
    //0 = Token Auth Radio value 
    const [pagerDutyAuthVal, setPagerDutyAuthVal] = useState(() => props.config ? props.config.loginType === "TOKEN" ? 0 : 1 : 0);
    const thisRef = useRef();
    const { sessionData } = useActiveUserContext();
    
    useImperativeHandle(ref,() => ({
        ...thisRef
    }))

    const authRadio = useMemo(() => (
        <>
        <Radio.Group onChange={(e) => {
            setAuth(e.target.value);
            form.setFieldsValue({version: e.target.value ? "V3" : "V2"});
        }}
            defaultValue={auth}>
                <Radio value={0}>{name === "PagerDuty" ? 'Token Auth' :'Basic Auth'} {name === "FreshService" && freshServiceNoticePopOver(freshServiceNote)} </Radio>
                <Radio value={1}>{name === "PagerDuty" ? 'OAuth' :'Token Auth'}</Radio>
        </Radio.Group>
        </>
    ), [auth, form, name]);

    const pagerDutyAuthRadio = useMemo(() => (
        <>
        <Radio.Group onChange={(e) => {
            setPagerDutyAuthVal(e.target.value);
            form.setFieldsValue({version: e.target.value ? "V3" : "V2"});
        }}
            defaultValue={pagerDutyAuthVal}>
                <Radio value={0}>{'Token Auth'}</Radio>
                {!sessionData.isCustomerDeployedInstance && <Radio value={1}>{'OAuth'}</Radio>}
        </Radio.Group>
        </>
    ), [pagerDutyAuthVal, form, sessionData.isCustomerDeployedInstance]);
    
    const showPagerDutyPopup = () => {
        ModalUtil.show({
            content: <PagerDutyOAuthPopup {...props.config}/>,
            afterClose: (failed, loginId) => {
                if(!failed){
                    props.onUpdateConfigComponent && props.onUpdateConfigComponent(true)
                    message.success(`${name} OAuth Login Configuration created`);
                    ModalUtil.show({
                        content: <JiraMappingForm onUpdateConfigComponent={props.onUpdateConfigComponent} name={name} loginConfigId={props.config?.id ?? loginId} />
                    })
                };
                thisRef.current && thisRef.current.close();
            }
        })
    }

    const jiraFields = [{
        name: "name",
        label: "Name",
        props: {
            autoFocus: true
        },
        colSpan: 12,
        rules: [
            {
                required: true,
                message: "Name is required"
            },
            duplicateNameValidator({list: loginConfigList}),
            nameRegexValidator
        ],
     }, {
        name: "url",
        label: "URL",
        colSpan: 12,
        required: true,
        rules: [urlRegexValidator()]
    },{
        type: 'Label',
        value: authRadio,
    }, auth ? {
        name: "email",
        label: "Email",
        colSpan: 12,
        required: true,
        rules: [{
            type: "email",
            message: "Invalid email"
        }]
    }: {
        name: "userName",
        label: "User Name",
        placeholder: "User Name",
        colSpan: 12,
        required: true
    }, {
        name: "apiToken",
        label: auth ? "API Token" : "Password",
        type: FieldTypes.PASSWORD,
        colSpan: 12,
        required: true
    }, {
        name: "version",
        label: "Version",
        colSpan: 12,
        disabled: true
    }
    ]

    const onOk = async () => {
        await form.validateFields();
        try{
            const toolName = name.toUpperCase().replace(' ', '_');
            let payload = form.getFieldsValue();

            if(toolName === 'PAGERDUTY'){
                payload = {
                    ...payload,
                    version: 'V2',
                }
            }
            if((auth === 1 && toolName === "SERVICE_NOW")){
                payload = {
                    ...payload,
                    tokenType: "ACCESS_TOKEN",
                    version: 'V2',
                }
            }
            const response = await ToolsIntegrationsAPI[!isEdit ? "postTicketSystemToolLoginDetails" : "updateTicketSystemToolLoginDetails"](toolName, payload, props.config?.id).promise;

            message.success(`${name} Login Configuration created`)
            const MappingFormComponent = name === "FreshService" ? FreshServiceMappingForm : JiraMappingForm;
            if(!isEdit){
                if (_.isFunction(handleMapping)) {
                    handleMapping(response);
                } else {
                    ModalUtil.show({
                        content: <MappingFormComponent onUpdateConfigComponent={props.onUpdateConfigComponent} name={name} url={name === "GitLab Issues" && response?.url} loginConfigId={response.id} />
                    })
                }
            }
            props.onUpdateConfigComponent && props.onUpdateConfigComponent(true);
        }catch(err){
            console.log(err);
            if(!_.isEmpty(err.response.data.errors)){
                const {field, defaultMessage} = err.response?.data?.errors[0]
                form.setFields([
                    {
                      name: field,
                      errors: [defaultMessage],
                    },
                ]);   
            }
            return Promise.reject(err.response?.data?.message);
        }
    };

    function getFields(name) {
        let newFields = fields;
        switch(name){
            case "Jira":
            case "FreshService":
                newFields = jiraFields;
                break;
            case "Service Now":
                newFields = serviceNowFields;
                break;
            case "PagerDuty":
                newFields = pagerDutyFields;
                break;
            case "Shortcut":
                newFields = shortcutFields;
                break;
            case "GitLab Issues":
                newFields = gitlabIssuesFields;
                break;
            case "Connect All":
                newFields = connectAllFields;
                break;
            default:
                return newFields
        }
        const [field] = newFields;
        field.props = {...(field.props || {}), autoFocus: true }
        return newFields
    }

    const modalProps = {
        title: <>{isEdit ? "Edit" : "Add"} {name} Login Credentials </>,
        width: MODAL_SIZE_MEDIUM,
        disabled: !hasPermission(isEdit ? PermissionSetMap.EDIT_TOOL_CONFIG : PermissionSetMap.WRITE_TOOLS),
        onOk: onOk
    }


    const gitlabIssuesFields = [{
        name: "name",
        label: "Name",
        props: {
            autoFocus: true
        },
        colSpan: 12,
        rules: [
            {
                required: true,
                message: "Name is required"
            },
            duplicateNameValidator({list: loginConfigList}),
            nameRegexValidator
        ],
     },{
        name: "userName",
        label: "User Name",
        placeholder: "User Name",
        colSpan: 12,
        required: true
    }, {
        name: "apiToken",
        label: "API Token",
        type: FieldTypes.PASSWORD,
        colSpan: 12,
        required: true
    }, {
        name: "url",
        label: "Host Url",
        rules: [urlRegexValidator()],
        colSpan: 12,
        required: true,
    },]
    
    const serviceNowFields = useMemo(() => {
        const fields = [{
            name: "name",
            label: "Name",
            colSpan: 12,
            props: {
                autoFocus: true,
            },
            rules: [
                {
                    required: true,
                    message: "Name is required"
                },
                duplicateNameValidator({list: loginConfigList}),
                nameRegexValidator
            ],
         }, {
            name: "url",
            label: "URL",
            colSpan: 12,
            required: true,
            rules: [urlRegexValidator()],
        }, {
            name: "userName",
            label: "User Name",
            colSpan: 12,
            required: true,
        },  {
            name: "apiToken",
            label: "Password",
            type: FieldTypes.PASSWORD,
            colSpan: 12,
            required: true,
        }, {
            name: "version",
            label: "Version",
            colSpan: 12,
            type: FieldTypes.SELECT,
            disabled: true,
        }]

        return fields;
    }, [loginConfigList]);

    const pagerDutyFields = [{
        type: 'Label',
        value: pagerDutyAuthRadio,
      },
      ...(pagerDutyAuthVal ? [] :
        [{
          name: "name",
          label: "Name",
          colSpan: 12,
          props: {
            autoFocus: true
          },
          required: true,
          rules: [duplicateNameValidator({list: loginConfigList}),
            nameRegexValidator
          ],
        }, {
          name: "email",
          label: "Email",
          colSpan: 12,
          required: true,
        }, {
          name: "apiToken",
          label: "Api Token",
          type: FieldTypes.PASSWORD,
          colSpan: 12,
          required: true
        }]
      )
    ]


    const shortcutFields = [{
        name: "name",
        label: "Name",
        colSpan: 12,
        props: {
            autoFocus: true
        },
        required: true,
        rules: [
            duplicateNameValidator({list: loginConfigList}),
            nameRegexValidator
        ],
     },{
        name: "apiToken",
        label: "API Token",
        type: FieldTypes.PASSWORD,
        colSpan: 12,
        required: true
    },{
        name: "email",
        label: "Email",
        colSpan: 12,
        required: true,
    },{
        name: "version",
        label: "Version",
        colSpan: 12,
        disabled:true
    }, ];

    const connectAllFields = [{
        name: "name",
        label: "Name",
        props: {
            autoFocus: true
        },
        colSpan: 12,
        required: true,
        rules: [
            duplicateNameValidator({list: loginConfigList}),
            nameRegexValidator
        ],
     }]
    
    const pagerDutyAuth = name === 'PagerDuty' && pagerDutyAuthVal;
    return (
        <BaseModal
            modalProps={ pagerDutyAuth ? { ...modalProps, footer: null } : modalProps}
            ref={thisRef}
            afterClose={props.afterClose}
        >  
            <Form
                initialValues={props.config || (name === "GitLab Issues" && {url: 'https://gitlab.com'}) || {version: name === "Service Now" || name === "FreshService" || name === "Shortcut" ? "V2" : "V3"}}
                form={form}
                layout="vertical"
            >
                {generateFormFields(getFields(name))}
                { pagerDutyAuth ? <Button onClick={showPagerDutyPopup} type='primary'>Connect to PagerDuty</Button> : null}
            </Form>
        </BaseModal>
    )
}

export default forwardRef(CommonTicketingForm);
